"use client";

import { useState, type AnchorHTMLAttributes, type FormEvent, type ReactNode } from "react";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import type {
  CSearchContent,
  CSearchGroup,
  CSearchResultItem,
  CSearchCatalogItem,
} from "@/lib/api/cms-catalog";
import { useT } from "@/lib/i18n/context";
import { cSearchTranslations, type CSearchTranslations } from "./CSearch.i18n";
import { extractInternalHref } from "@/lib/utils/extractInternalHref";
import styles from "./CSearch.module.css";

/**
 * CSearch - strona wyników wyszukiwarki (`c_search`).
 *
 * **Data source:** `C_SEARCH_CONTROLLER::getApiContent()` (SSR przez `?q=`)
 * lub endpoint `IAPI_search`. Payload: {@link CSearchContent}.
 *
 * Render: formularz wyszukiwania (re-search z poziomu wyników) + sekcje wyników:
 *   - `groups[]`   - treść komponentów, pogrupowana per symbol (etykiety z i18n);
 *                    elementy otagowane są wtopione w grupę swojego komponentu
 *   - `catalogs[]` - dopasowania nazw katalogów
 *
 * **Security:** `text` (snippet) bywa HTML z admin panelu - trusted, OK dla
 * `dangerouslySetInnerHTML` (spójnie z CNews).
 */
export function CSearch({ content }: CSearchProps) {
  const t = useT(cSearchTranslations);

  if (!content || typeof content !== "object") {
    return (
      <div className={styles.root}>
        <div className={styles.empty}>{t.emptyData}</div>
      </div>
    );
  }

  const query = content.query ?? "";
  const groups: CSearchGroup[] = Array.isArray(content.groups) ? content.groups : [];
  const catalogs: CSearchCatalogItem[] = Array.isArray(content.catalogs) ? content.catalogs : [];
  const wrongWords: string[] = Array.isArray(content.wrong_words) ? content.wrong_words : [];
  const total = content.meta?.total ?? 0;

  const hasQuery = query.trim() !== "";
  const hasResults = total > 0;

  return (
    <div className={styles.root}>
      <SearchForm initialQuery={query} t={t} />

      {wrongWords.length > 0 && (
        <p className={styles.wrongWords} role="status">
          {t.wrongWords} {wrongWords.join(", ")}
        </p>
      )}

      {!hasQuery ? (
        <div className={styles.empty}>{t.noQuery}</div>
      ) : !hasResults ? (
        <div className={styles.empty}>{t.noResults}</div>
      ) : (
        <div className={styles.results}>
          <p className={styles.summary}>
            {t.resultsFor} <strong>{query}</strong> {t.resultsCount(total)}
          </p>

          {groups.map((group) => (
            <ResultSection
              key={group.symbol}
              title={t.groupLabel(group.symbol)}
              items={group.items}
            />
          ))}

          {catalogs.length > 0 && (
            <section className={styles.section}>
              <h2 className={styles.sectionTitle}>{t.catalogsTitle}</h2>
              <ul className={styles.catalogList}>
                {catalogs.map((c, i) => (
                  <li key={`${c.href}-${i}`} className={styles.catalogItem}>
                    <SmartLink href={c.href} className={styles.catalogLink}>
                      {c.name}
                    </SmartLink>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

export type CSearchProps = {
  content: CSearchContent;
};

/* ─── Search form ──────────────────────────────────────────────────────── */

function SearchForm({
  initialQuery,
  t,
}: {
  initialQuery: string;
  t: CSearchTranslations;
}) {
  const router = useRouter();
  const pathname = usePathname();
  const [value, setValue] = useState(initialQuery);

  function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const q = value.trim();
    /* Nawigacja na tę samą stronę z `?q=` - SSR backend zwróci wyniki. Pusta
       fraza czyści wyniki (URL bez query). */
    router.push(q === "" ? pathname : `${pathname}?q=${encodeURIComponent(q)}`);
  }

  return (
    <form className={styles.form} role="search" onSubmit={onSubmit}>
      <label className={styles.formLabel} htmlFor="csearch-q">
        {t.inputLabel}
      </label>
      <div className={styles.formRow}>
        <input
          id="csearch-q"
          type="search"
          name="q"
          className={styles.input}
          placeholder={t.inputPlaceholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="off"
        />
        <button type="submit" className={styles.submit}>
          {t.submit}
        </button>
      </div>
    </form>
  );
}

/* ─── Result section (grupa komponentu) ───────────────────────────────────── */

function ResultSection({
  title,
  items,
}: {
  title: string;
  items: CSearchResultItem[];
}) {
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{title}</h2>
      <ul className={styles.list}>
        {items.map((item, i) => (
          <ResultCard key={`${item.href}-${i}`} item={item} />
        ))}
      </ul>
    </section>
  );
}

function ResultCard({ item }: { item: CSearchResultItem }) {
  return (
    <li className={styles.card}>
      <SmartLink href={item.href} className={styles.cardLink} aria-label={item.name}>
        {item.picture && (
          <div className={styles.thumbWrap}>
            {/* Native <img> - `picture` to URL plików z Imperial backendu
                (cross-origin, dynamiczne formaty). Spójnie z CNews. */}
            <img src={item.picture} alt={item.name} className={styles.thumb} loading="lazy" />
          </div>
        )}
        <div className={styles.body}>
          <h3 className={styles.title}>{item.name}</h3>
          {item.text && (
            /* `<div>` nie `<p>` - text bywa HTML (`<p>...`) -> uniknięcie
               zagnieżdżonych <p> (hydration mismatch). */
            <div
              className={styles.snippet}
              dangerouslySetInnerHTML={{ __html: item.text }}
            />
          )}
        </div>
      </SmartLink>
    </li>
  );
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

/**
 * Link wewnętrzny przez `<Link>` (SPA nav) lub natywny `<a>` dla URL-i
 * cross-domain / nietypowych. Skopiowane z CNews/WMenu.
 */
function SmartLink({
  href,
  className,
  children,
  ...rest
}: {
  href: string;
  className?: string;
  children: ReactNode;
} & AnchorHTMLAttributes<HTMLAnchorElement>) {
  const internal = extractInternalHref(href);
  if (internal !== null) {
    return (
      <Link href={internal} className={className} {...rest}>
        {children}
      </Link>
    );
  }
  return (
    <a href={href} className={className} {...rest}>
      {children}
    </a>
  );
}
